// @flow
import zentakeApi from 'api'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSaveContext } from 'components/ResourceFill/providers/ResourceSaveProvider'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import * as React from 'react'

import type { NavigationContext } from '../types/navigation-context'

type Props = {
  children: React.Node,
}

export const ResourceNavigationContext: React.Context<?NavigationContext> =
  React.createContext(null)

function ResourceNavigationProvider({ children }: Props): React.Node {
  const { mode, isPacket, formIndex, setFormIndex } =
    React.useContext(ResourceSharedContext) || {}

  const { sessions, sessionId, currentSession, setCurrentSession } =
    React.useContext(ResourceSessionsContext) || {}

  const { updateResponse, response } =
    React.useContext(ResourceResponseContext) || {}

  const { saving, save } = React.useContext(ResourceSaveContext) || {}

  const [formsCount, setFormsCount] = React.useState(0)

  const getInitialResponseIndex = React.useCallback(() => {
    if (!sessions || !sessionId) return 0

    const current = sessions?.[sessionId]
    if (mode === 'office') {
      return 0
    }

    return Math.max(
      current.responses.findIndex((r) => !r.is_submitted),
      0
    ) || isPacket
      ? current?.packetResponse?.form_index || 0
      : 0
  }, [isPacket, mode, sessionId, sessions])

  const updateFormIndex = React.useCallback(
    (index: number) => {
      if (mode === 'client') {
        if (isPacket) {
          zentakeApi.updatePacketResponseVisited(
            currentSession?.packetResponse?.id,
            index
          )
        } else {
          zentakeApi.updateResponseVisited(response?.id, { formIndex: index })
        }
      }
    },
    [currentSession?.packetResponse?.id, isPacket, mode, response?.id]
  )

  const increaseFormIndex = React.useCallback(() => {
    if (!updateResponse) return
    if (!setFormIndex) return
    if (formIndex === null || formIndex === undefined) return

    let newIndex = formIndex + 1 > formsCount ? formIndex : formIndex + 1

    if (mode === 'office' && formIndex === formsCount - 1) {
      newIndex = 0
    }

    updateResponse(newIndex)
    updateFormIndex(newIndex)
    setFormIndex(newIndex)
  }, [
    updateResponse,
    setFormIndex,
    formIndex,
    formsCount,
    mode,
    updateFormIndex,
  ])

  const decreaseFormIndex = React.useCallback(() => {
    if (formIndex === null || formIndex === undefined) return
    if (!updateResponse) return
    if (!setFormIndex) return

    const newIndex = formIndex - 1 < 0 ? 0 : formIndex - 1

    updateResponse(newIndex)
    setFormIndex(newIndex)
  }, [updateResponse, formIndex, setFormIndex])

  const onPrevForm = React.useCallback(async () => {
    if (saving) return
    if (!save) return

    const response = await save({ validate: false })

    if (response) {
      decreaseFormIndex()
    }

    return response
  }, [decreaseFormIndex, save, saving])

  const onNextForm = React.useCallback(
    async ({ submit = false, lockInternalForm = false }) => {
      if (saving) return
      if (!save) return

      const updatedSession = await save({
        validate: true,
        submit,
        lockInternalForm,
      })

      if (updatedSession) {
        increaseFormIndex()
      }

      return updatedSession
    },
    [increaseFormIndex, save, saving]
  )

  React.useEffect(() => {
    if (!setCurrentSession) return
    if (!setFormIndex) return
    if (!updateResponse) return
    if (!setFormsCount) return
    if (!sessionId) return

    if (
      getInitialResponseIndex === null ||
      getInitialResponseIndex === undefined
    )
      return

    const current = sessions?.[sessionId]

    if (!current) return

    setCurrentSession(current)

    const initialResponseIndex = getInitialResponseIndex()

    setFormIndex(initialResponseIndex)

    updateResponse(initialResponseIndex)
    setFormsCount(current?.responses?.length || 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const value = React.useMemo(
    () => ({
      getInitialResponseIndex,
      setFormsCount,
      onNextForm,
      updateFormIndex,
      onPrevForm,
    }),
    [getInitialResponseIndex, onNextForm, onPrevForm, updateFormIndex]
  )

  return (
    <ResourceNavigationContext.Provider value={value}>
      {children}
    </ResourceNavigationContext.Provider>
  )
}

export default ResourceNavigationProvider
